import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, CircularProgress, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import {
  findAcoesRelatorioGeral,
  findRelatorioGeralAcao,
  findRelatorioGeralAcaoCount,
  findTurmasRelatorioGeral,
} from "API/apiRelatoriosTurma";
import { useAppFeedback } from "hooks/useAppFeedback";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { formatarCpf } from "utils/Masks";
import { getReportPeriodError } from "utils/reportPeriod";
import { addGovernmentReportLogo, prependGovernmentReportHeader } from "utils/reportExportHeader";
import styles from "../Relatorios.module.css";

type RelatorioGeralAcaoProps = {
  selectedReportLabel: string;
};

type PresencaItem = {
  data_presenca?: string | null;
  tipo?: string | null;
};

type RelatorioItem = {
  acao?: {
    nome?: string | null;
  };
  turma?: {
    nome?: string | null;
    criterio_aprovacao?: {
      tipo?: string | null;
      pontuacao_aprovado?: number | null;
      porcentagem_aprovacao?: number | null;
    };
  };
  inscricao?: {
    created_at?: string | null;
    ambito_id?: number | null;
    municipio_nome?: string | null;
    ambito_nome?: string | null;
    lotacao_nome?: string | null;
    vinculo_nome?: string | null;
    funcao_nome?: string | null;
    superintendencia_nome?: string | null;
    unidade_nome?: string | null;
    diretoria_regional_nome?: string | null;
    diretoria_interna_nome?: string | null;
    orgao_nome?: string | null;
    instituicao_nome?: string | null;
    resultado?: {
      nota?: number | null;
      status?: string | null;
    };
    presencas?: PresencaItem[];
    usuario?: {
      nome?: string | null;
      cpf?: string | null;
      email?: string | null;
      dados_pessoais?: {
        nome_social?: string | null;
        data_nascimento?: string | null;
        identidade_genero?: string | null;
        identidade_genero_outros?: string | null;
        cor_raca?: string | null;
        escolaridade?: string | null;
        profissao?: string | null;
        profissao_outros?: string | null;
        possui_deficiencia?: boolean | null;
        deficiencia_outros?: string | null;
        estrutura_participacao?: string | null;
      };
      dados_contato?: {
        email_institucional?: string | null;
        telefone_celular?: string | null;
        possui_whatsapp?: boolean | null;
        telefone_institucional?: string | null;
        telefone_residencial?: string | null;
      };
      deficiencias?: Array<{
        deficiencia?: string | null;
      }>;
      areas_atuacao?: Array<{
        label?: string | null;
      }>;
    };
  };
};

type ExportColumn = {
  label: string;
  value: (item: RelatorioItem) => string | number | null | undefined;
  statusFill?: (item: RelatorioItem) => string | undefined;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const formatBoolean = (value?: boolean | null) => (value ? "Sim" : "Não");

const joinValues = (values: Array<string | null | undefined>) =>
  values.map((value) => value?.trim()).filter(Boolean).join(", ");

const getCriterioAprovacao = (item: RelatorioItem) => {
  const criterio = item.turma?.criterio_aprovacao;
  const tipo = criterio?.tipo;

  if (tipo === "PRESENCA") {
    return `Apenas Frequência >= ${criterio?.porcentagem_aprovacao ?? 0}%`;
  }

  if (tipo === "PRESENCA_E_ATIVIDADE") {
    return `Frequência >= ${criterio?.porcentagem_aprovacao ?? 0}% e Nota >= ${criterio?.pontuacao_aprovado ?? 0}`;
  }

  if (tipo === "ATIVIDADE_AVALIATIVA") {
    return `Nota >= ${criterio?.pontuacao_aprovado ?? 0}`;
  }

  return "";
};

const getDatasPresenca = (presencas?: PresencaItem[]) =>
  (presencas || [])
    .filter((item) => item.tipo === "PRESENTE" && item.data_presenca)
    .map((item) => formatDate(item.data_presenca))
    .filter(Boolean)
    .join(", ");

const getDeficiencias = (item: RelatorioItem) => {
  const possuiDeficiencia = item.inscricao?.usuario?.dados_pessoais?.possui_deficiencia;
  if (!possuiDeficiencia) {
    return "";
  }

  return joinValues([
    ...(item.inscricao?.usuario?.deficiencias || []).map((deficiencia) => deficiencia.deficiencia),
    item.inscricao?.usuario?.dados_pessoais?.deficiencia_outros,
  ]);
};

const exportColumns: ExportColumn[] = [
  { label: "AÇÃO", value: (item) => item.acao?.nome || "" },
  { label: "TURMA", value: (item) => item.turma?.nome || "" },
  { label: "DATA DE INSCRIÇÃO", value: (item) => formatDate(item.inscricao?.created_at || "") },
  { label: "CPF", value: (item) => formatarCpf(item.inscricao?.usuario?.cpf || "") },
  { label: "NOME COMPLETO", value: (item) => item.inscricao?.usuario?.nome || "" },
  { label: "NOME SOCIAL", value: (item) => item.inscricao?.usuario?.dados_pessoais?.nome_social || "" },
  { label: "DATA DE NASCIMENTO", value: (item) => formatDate(item.inscricao?.usuario?.dados_pessoais?.data_nascimento) },
  {label: "IDENTIDADE DE GÊNERO", value: (item) => item.inscricao?.usuario?.dados_pessoais?.identidade_genero || "",},
  { label: "COR/RAÇA", value: (item) => item.inscricao?.usuario?.dados_pessoais?.cor_raca || "" },
  {
    label: "ESCOLARIDADE",
    value: (item) => item.inscricao?.usuario?.dados_pessoais?.escolaridade || "",
  },
  {
    label: "PROFISSÃO",
    value: (item) =>
      item.inscricao?.usuario?.dados_pessoais?.profissao_outros ||
      item.inscricao?.usuario?.dados_pessoais?.profissao ||
      "",
  },
  { label: "E-MAIL", value: (item) => item.inscricao?.usuario?.email || "" },
  {
    label: "E-MAIL INSTITUCIONAL",
    value: (item) => item.inscricao?.usuario?.dados_contato?.email_institucional || "",
  },
  {
    label: "TELEFONE CELULAR",
    value: (item) => item.inscricao?.usuario?.dados_contato?.telefone_celular || "",
  },
  {
    label: "É WHATSAPP?",
    value: (item) => formatBoolean(item.inscricao?.usuario?.dados_contato?.possui_whatsapp),
  },
  {
    label: "TELEFONE INSTITUCIONAL",
    value: (item) => item.inscricao?.usuario?.dados_contato?.telefone_institucional || "",
  },
  {
    label: "TELEFONE FIXO",
    value: (item) => item.inscricao?.usuario?.dados_contato?.telefone_residencial || "",
  },
  {
    label: "POSSUI DEFICIÊNCIA",
    value: (item) => formatBoolean(item.inscricao?.usuario?.dados_pessoais?.possui_deficiencia),
  },
  { label: "DEFICIÊNCIA", value: (item) => getDeficiencias(item) },
  {
    label: "NECESSITA ESTRUTURA ESPECÍFICA",
    value: (item) => item.inscricao?.usuario?.dados_pessoais?.estrutura_participacao || "",
  },
  { label: "ÂMBITO DE ATUAÇÃO", value: (item) => item.inscricao?.ambito_nome || "" },
  { label: "MUNICIPIO DE ATUAÇÃO", value: (item) => item.inscricao?.municipio_nome || "" },
  { label: "ÓRGÃO DE ATUAÇÃO (MUNICIPAL)", value: (item) => item.inscricao?.orgao_nome || "" },
  {
    label: "OUTRAS SECRETARIAS/ÓRGÃOS DA PREFEITURA (MUNICIPAL)",
    value: (item) => (item.inscricao?.ambito_id === 2 ? item.inscricao?.instituicao_nome || "" : ""),
  },
  {
    label: "OUTROS ÓRGÃOS/INSTITUIÇÕES (ESTADUAL)",
    value: (item) => (item.inscricao?.ambito_id === 1 ? item.inscricao?.instituicao_nome || "" : ""),
  },
  { label: "LOTAÇÃO (ESTADUAL)", value: (item) => item.inscricao?.lotacao_nome || "" },
  { label: "UNIDADE CREAS REGIONAL (ESTADUAL)", value: (item) => item.inscricao?.unidade_nome || "" },
  {
    label: "DIRETORIA REGIONAL (ESTADUAL)",
    value: (item) => item.inscricao?.diretoria_regional_nome || "",
  },
  {
    label: "SUPERINTENDÊNCIA (ESTADUAL)",
    value: (item) => item.inscricao?.superintendencia_nome || "",
  },
  {
    label: "DIRETORIA VINCULADA (ESTADUAL)",
    value: (item) => item.inscricao?.diretoria_interna_nome || "",
  },
  {
    label: "ÁREA DE ATUAÇÃO",
    value: (item) => joinValues((item.inscricao?.usuario?.areas_atuacao || []).map((area) => area.label)),
  },
  { label: "FUNÇÃO DESEMPENHADA", value: (item) => item.inscricao?.funcao_nome || "" },
  { label: "VÍNCULO EMPREGATÍCIO", value: (item) => item.inscricao?.vinculo_nome || "" },
  { label: "CRITÉRIO DE APROVAÇÃO DA TURMA", value: (item) => getCriterioAprovacao(item) },
  { label: "DATA DA PRESENÇA", value: (item) => getDatasPresenca(item.inscricao?.presencas) },
  { label: "NOTA", value: (item) => item.inscricao?.resultado?.nota ?? "" },
  {
    label: "SITUAÇÃO (APROVADO/REPROVADO)",
    value: (item) => item.inscricao?.resultado?.status || "",
    statusFill: (item) => {
      const status = item.inscricao?.resultado?.status;
      if (status === "APROVADO") return "FFDCFCE7";
      if (status === "REPROVADO") return "FFFDE2E2";
      return undefined;
    },
  },
  {
    label: "PORCENTAGEM DO CRITÉRIO DE APROVAÇÃO ATINGIDA (PRESENÇA, NOTA OU PRESENÇA+NOTA)",
    value: () => "",
  },
];

const RelatorioGeralAcao = ({ selectedReportLabel }: RelatorioGeralAcaoProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [acoesSelecionadas, setAcoesSelecionadas] = useState<Array<number | string>>([]);
  const [turmasSelecionadas, setTurmasSelecionadas] = useState<Array<number | string>>([]);
  const [acoesOptions, setAcoesOptions] = useState<MultiSelectOption[]>([]);
  const [turmasOptions, setTurmasOptions] = useState<MultiSelectOption[]>([]);
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const periodError = getReportPeriodError(dataInicial, dataFinal);
  const periodValidationError = dataInicial && dataFinal ? periodError : null;
  const hasValidPeriod = !periodError;
  const canSelectAcoes = hasValidPeriod && !loadingAcoes;
  const canSelectTurmas = acoesSelecionadas.length > 0 && !loadingTurmas;

  const acoesResumo = acoesSelecionadas.length;
  const turmasResumo = turmasSelecionadas.length;

  const selectedActionIds = useMemo(
    () => acoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [acoesSelecionadas],
  );

  const selectedTurmaIds = useMemo(
    () => turmasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [turmasSelecionadas],
  );

  useEffect(() => {
    if (!hasValidPeriod) {
      setAcoesOptions([]);
      setTurmasOptions([]);
      setAcoesSelecionadas([]);
      setTurmasSelecionadas([]);
      setEstimatedCount(null);
      return;
    }

    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAcoesRelatorioGeral(dataInicial, dataFinal);
        setAcoesOptions((response.acoes || []).map((item) => ({ id: item.id, nome: item.nome })));
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        setTurmasOptions([]);
        setEstimatedCount(null);
      } catch (error) {
        setAcoesOptions([]);
        setTurmasOptions([]);
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        setEstimatedCount(null);
        showError("Não foi possível carregar as ações do relatório.", error);
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, [dataFinal, dataInicial, hasValidPeriod, showError]);

  useEffect(() => {
    if (!hasValidPeriod || selectedActionIds.length === 0) {
      setTurmasOptions([]);
      setTurmasSelecionadas([]);
      return;
    }

    const loadTurmas = async () => {
      try {
        setLoadingTurmas(true);
        const response = await findTurmasRelatorioGeral(selectedActionIds, dataInicial, dataFinal);
        const turmas = response.turmas || [];
        setTurmasOptions(turmas.map((item) => ({ id: item.id, nome: item.nome })));
        setTurmasSelecionadas((current) =>
          current.filter((id) => turmas.some((turma) => turma.id === Number(id))),
        );
      } catch (error) {
        setTurmasOptions([]);
        setTurmasSelecionadas([]);
        showError("Não foi possível carregar as turmas do relatório.", error);
      } finally {
        setLoadingTurmas(false);
      }
    };

    void loadTurmas();
  }, [dataFinal, dataInicial, hasValidPeriod, selectedActionIds, showError]);

  useEffect(() => {
    if (!hasValidPeriod || selectedActionIds.length === 0) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioGeralAcaoCount({
          acoes_ids: selectedActionIds,
          turmas_ids: selectedTurmaIds.length > 0 ? selectedTurmaIds : undefined,
        });
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Não foi possível calcular o resultado estimado do relatório.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [hasValidPeriod, selectedActionIds, selectedTurmaIds, showError]);

  const handleClear = () => {
    setDataInicial("");
    setDataFinal("");
    setAcoesSelecionadas([]);
    setTurmasSelecionadas([]);
    setAcoesOptions([]);
    setTurmasOptions([]);
    setEstimatedCount(null);
  };

  const buildWorkbook = (rows: RelatorioItem[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Geral da Acao", {
      views: [{ state: "frozen", xSplit: 5, ySplit: 5 }],
    });

    worksheet.columns = exportColumns.map((column) => ({
      header: column.label,
      key: column.label,
      width: Math.max(18, Math.min(40, column.label.length + 4)),
    }));

    const headerRowNumber = prependGovernmentReportHeader(worksheet, exportColumns.length) + 1;
    const headerRow = worksheet.getRow(headerRowNumber);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF004D40" },
      };
      cell.font = {
        bold: true,
        color: { argb: "FFFFFFFF" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
    });

    rows.forEach((row) => {
      const values = exportColumns.map((column) => column.value(row) ?? "");
      const excelRow = worksheet.addRow(values);

      exportColumns.forEach((column, columnIndex) => {
        const fillColor = column.statusFill?.(row);
        if (!fillColor) {
          return;
        }

        excelRow.getCell(columnIndex + 1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
      });
    });

    worksheet.autoFilter = {
      from: { row: headerRowNumber, column: 1 },
      to: { row: headerRowNumber, column: exportColumns.length },
    };

    /*
    Bloco antigo com SheetJS mantido comentado para teste temporario:

    const header = exportColumns.map((column) => column.label);
    const body = rows.map((row) => exportColumns.map((column) => column.value(row) ?? ""));
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);

    worksheet["!cols"] = exportColumns.map((column) => ({
      wch: Math.max(18, Math.min(40, column.label.length + 4)),
    }));

    (worksheet as any)["!freeze"] = { xSplit: 5, ySplit: 1 };
    */

    return workbook;
  };

  const handleGenerateReport = async () => {
    if (periodError) {
      showError(periodError);
      return;
    }

    if (selectedActionIds.length === 0) {
      showError("Selecione ao menos uma ação para gerar o relatório.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await findRelatorioGeralAcao({
        acoes_ids: selectedActionIds,
        turmas_ids: selectedTurmaIds.length > 0 ? selectedTurmaIds : undefined,
        perPage: 999999,
      });

      const rows = response.data || [];
      const workbook = buildWorkbook(rows);
      await Promise.all(workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet)));
      const excelBuffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "relatorio_geral_acao.xlsx",
      );

      showSuccess("Relatório gerado com sucesso.");
    } catch (error) {
      showError("Não foi possível gerar o relatório.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Período <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Data inicial</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataInicial}
                error={Boolean(periodValidationError)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataInicial(event.target.value)}
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                }}
              />
            </div>

            <div>
              <label className={stylesInput.titleInputSoft}>Data final</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataFinal}
                error={Boolean(periodValidationError)}
                helperText={periodValidationError || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataFinal(event.target.value)}
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) ação(ões) <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              hasValidPeriod ? "Selecione uma ou mais ações" : "Preencha primeiro a data inicial e a data final"
            }
            fullWidth
            value={acoesSelecionadas}
            onChange={setAcoesSelecionadas}
            options={acoesOptions}
            disabled={!canSelectAcoes}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) turma(s) <span className={styles.optionalText}>(opcional)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              selectedActionIds.length > 0
                ? "Selecione uma ou mais turmas (deixe vazio para todas as turmas da ação selecionada)"
                : "Selecione primeiro uma ou mais ações"
            }
            fullWidth
            value={turmasSelecionadas}
            onChange={setTurmasSelecionadas}
            options={turmasOptions}
            disabled={!canSelectTurmas}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            Ao deixar o campo de turmas vazio, o sistema ira considerar todas as turmas das ações selecionadas.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>

          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Período</div>
                <div className={styles.selectionSummaryValue}>
                  {formatDate(dataInicial)} a {formatDate(dataFinal)}
                </div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <AppsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Ações selecionadas</div>
                <div className={styles.selectionSummaryValue}>{acoesResumo} acao(oes)</div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <GroupsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Turmas selecionadas</div>
                <div className={styles.selectionSummaryValue}>
                  {turmasResumo > 0 ? `${turmasResumo} turma(s)` : "Todas as turmas"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioGeralAcao;
